import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { CompanyShell } from "@/components/CompanyShell";
import { Reorder } from "framer-motion";
import { SortableCard, ResetOrderButton, useLayoutOrder, applyVisibleReorder } from "@/components/Sortable";
import { PassportView } from "@/components/PassportView";
import { InfoTip } from "@/components/InfoTip";
import { JobCompare } from "@/components/JobCompare";
import { PositionsPanel } from "@/components/PositionsPanel";
import { StatusHistory } from "@/components/StatusHistory";
import { FirstDecisionDialog } from "@/components/FirstDecisionDialog";
import { InterviewPage } from "@/components/InterviewPage";
import { STATUS_META, REJECT_CONFIRM } from "@/lib/pipeline";
import { StatusBadge } from "@/components/StatusBadge";
import { ScoreRing } from "@/components/ScoreRing";
import { ArrowLeft, Mail, Loader2, Sparkles, Check, TriangleAlert, Send, Archive, ArchiveRestore, EyeOff, ShieldAlert, Lock, MessageSquarePlus, Pencil, Trash2, Layers, FileText, ExternalLink, Share2, Copy, XCircle, Clock, ScrollText, Camera } from "lucide-react";

const STATUS_OPTS = [
  { key: "active", label: "Aktívny", cls: "bg-emerald-600" },
  { key: "contacted", label: "Kontaktovaný", cls: "bg-blue-600" },
  { key: "inactive", label: "Neaktívny", cls: "bg-amber-500" },
  { key: "rejected", label: "Zamietnutý", cls: "bg-red-600" },
];

const APP_TYPE_OPTS = [
  { v: "active", l: "Aktívna prihláška" },
  { v: "sourced", l: "Proaktívne oslovený" },
  { v: "talent_pool", l: "Talent pool / databáza" },
  { v: "referral", l: "Odporúčanie" },
  { v: "unknown", l: "NEZAZNAMENANÉ" },
];
const LEGAL_BASIS_OPTS = [
  { v: "consent", l: "Súhlas (čl. 6/1a)" },
  { v: "legitimate_interest", l: "Oprávnený záujem (čl. 6/1f)" },
  { v: "contract", l: "Plnenie zmluvy (čl. 6/1b)" },
  { v: "legal_obligation", l: "Zákonná povinnosť (čl. 6/1c)" },
  { v: "unknown", l: "NEZAZNAMENANÉ" },
];
const PRIVACY_OPTS = [
  { v: "not_sent", l: "Neodoslané" },
  { v: "sent", l: "Odoslané" },
  { v: "acknowledged", l: "Potvrdené kandidátom" },
  { v: "unknown", l: "NEZAZNAMENANÉ" },
];

const PIPELINE_OPTS = ["new", "in_process", "interview", "hired", "rejected_retain", "rejected_shredded"]
  .map((v) => ({ v, l: STATUS_META[v].l, cls: STATUS_META[v].solid }));
const GDPR_BADGE = {
  ACTIVE: { l: "Aktívny súhlas", cls: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  EXPIRING_SOON: { l: "Vyprší čoskoro", cls: "bg-amber-50 text-amber-700 border-amber-100" },
  EXPIRED: { l: "Expirovaný", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  REVOKED: { l: "Odvolaný", cls: "bg-red-50 text-red-600 border-red-100" },
  PENDING: { l: "Čaká na súhlas", cls: "bg-blue-50 text-blue-700 border-blue-100" },
  NONE: { l: "Bez súhlasu", cls: "bg-slate-50 text-slate-500 border-slate-200" },
};

const CANDIDATE_SECTION_IDS = [
  "provenance", "ai_summary", "suitable_positions", "contact", "pipeline", "positions", "history",
  "consent", "notes", "share", "photo", "areas", "gdpr", "data_mgmt",
];

export default function CandidateView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("Dobrý deň, zaujal nás Váš profil na Profeeluj AI. Radi by sme Vás kontaktovali ohľadom pracovnej príležitosti.");
  const [sending, setSending] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const [shares, setShares] = useState([]);
  const [shareDays, setShareDays] = useState(14);
  const [shareLabel, setShareLabel] = useState("");
  const [creatingShare, setCreatingShare] = useState(false);
  const [newLink, setNewLink] = useState("");
  const [gdpr, setGdpr] = useState(null);
  const [savingGdpr, setSavingGdpr] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [privacyEmail, setPrivacyEmail] = useState("");
  const [sendingPrivacy, setSendingPrivacy] = useState(false);
  const [areas, setAreas] = useState([]);
  const [savingArea, setSavingArea] = useState(false);
  const photoRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [consents, setConsents] = useState([]);
  const [gdprStatus, setGdprStatus] = useState("NONE");
  const [pipelineBusy, setPipelineBusy] = useState(false);
  const [moveModal, setMoveModal] = useState(null);
  const [moveBusy, setMoveBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [decisionClosed, setDecisionClosed] = useState(false);
  const [histKey, setHistKey] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const { order: sectionOrder, setOrder: setSectionOrder, reset: resetSections } = useLayoutOrder("candidate_view", CANDIDATE_SECTION_IDS);

  const openPreview = async (type) => {
    try {
      const { data } = await api.get(`/company/candidate/${id}/email-preview?type=${type}`);
      setPreview(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const loadConsents = () => api.get(`/company/candidate/${id}/consents`)
    .then((r) => { setConsents(r.data.consents || []); setGdprStatus(r.data.gdpr_status || "NONE"); }).catch(() => {});
  useEffect(() => { loadConsents(); }, [id]); // eslint-disable-line

  const setPipeline = async (status) => {
    setPipelineBusy(true);
    try {
      const r = await api.put(`/company/candidate/${id}/pipeline-status`, { status });
      if (r.data.shredded) toast.success("Kandidát zamietnutý a údaje skartované");
      else if (status === "rejected_retain") {
        setMoveModal({ needs_consent: r.data.needs_consent, gdpr_status: r.data.gdpr_status });
      } else toast.success("Stav kandidáta aktualizovaný");
      await load(); await loadConsents(); setHistKey((k) => k + 1); window.dispatchEvent(new Event("db-changed"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPipelineBusy(false); }
  };

  const sendReply = async (reply_type, overrides = {}) => {
    setMoveBusy(true);
    try {
      const r = await api.post(`/company/candidate/${id}/auto-reply`, { reply_type, ...overrides });
      toast.success(r.data.email_sent ? "Odpoveď odoslaná kandidátovi" : "Odpoveď pripravená (email v sandboxe)");
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409) toast.info("Najprv vyžiadajte súhlas — kandidát ho nemá.");
      else toast.error(formatApiError(d));
    } finally { setMoveBusy(false); }
  };

  const sendFromPreview = async () => {
    if (!preview) return;
    const ov = { subject: preview.subject, body: preview.body_text };
    if (preview.type === "consent_request") await requestConsent(ov);
    else await sendReply(preview.type, ov);
    setPreview(null);
  };

  const requestConsent = async (overrides = {}) => {
    try {
      const r = await api.post(`/company/candidate/${id}/consent/request`, overrides);
      const url = `${window.location.origin}${r.data.path}`;
      try { await navigator.clipboard.writeText(url); } catch { /* noop */ }
      toast.success(r.data.email_sent ? "Žiadosť o súhlas odoslaná emailom" : "Žiadosť pripravená — odkaz skopírovaný (email v sandboxe)");
      await loadConsents();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const revokeConsent = async (consentId) => {
    if (!window.confirm("Odvolať / ukončiť uchovávanie tohto súhlasu?")) return;
    try { await api.post(`/company/candidate/${id}/consent/${consentId}/revoke`); toast.success("Súhlas odvolaný"); await loadConsents(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/company/candidate/${id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Fotografia nahraná"); await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingPhoto(false); if (photoRef.current) photoRef.current.value = ""; }
  };

  const load = () => api.get(`/company/candidate/${id}`).then((r) => setP(r.data)).catch(() => setP(false));
  const loadNotes = () => api.get(`/company/candidate/${id}/notes`).then((r) => setNotes(r.data.notes)).catch(() => {});
  const loadShares = () => api.get(`/company/candidate/${id}/shares`).then((r) => setShares(r.data.shares)).catch(() => {});
  useEffect(() => { load(); loadNotes(); loadShares(); }, [id]); // eslint-disable-line
  useEffect(() => { api.get("/company/areas").then((r) => setAreas(r.data.areas || [])).catch(() => {}); }, []);

  const changeArea = async (area) => {
    const current = p.areas || [];
    const next = current.includes(area) ? current.filter((a) => a !== area) : [...current, area];
    setSavingArea(true);
    try { await api.put(`/company/candidate/${id}/areas`, { areas: next }); toast.success("Oblasti aktualizované"); await load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingArea(false); }
  };

  useEffect(() => {
    if (p && p !== false) {
      const clean = (v) => (v && v !== "NEZAZNAMENANÉ" ? v : "");
      setGdpr({
        source: clean(p.source),
        application_type: p.application_type ?? "unknown",
        purpose: clean(p.purpose),
        legal_basis: p.legal_basis ?? "unknown",
        retention_date: p.retention_date ?? "",
        privacy_notice_status: p.privacy_notice_status ?? "unknown",
      });
    }
  }, [p]);

  const saveGdpr = async () => {
    setSavingGdpr(true);
    try {
      await api.put(`/company/candidate/${id}/gdpr`, gdpr);
      toast.success("GDPR údaje uložené");
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingGdpr(false); }
  };

  const sendPrivacy = async () => {
    if (!privacyEmail.trim()) return;
    setSendingPrivacy(true);
    try {
      const r = await api.post(`/company/candidate/${id}/privacy-notice`, { email: privacyEmail.trim() });
      toast.success(r.data.mock ? "Privacy Notice pripravené (mock režim – bez API kľúča)" : "Privacy Notice odoslané kandidátovi");
      setPrivacyOpen(false);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSendingPrivacy(false); }
  };

  const createShare = async () => {
    setCreatingShare(true);
    try {
      const r = await api.post(`/company/candidate/${id}/share`, { expires_days: Number(shareDays), label: shareLabel.trim() });
      const url = `${window.location.origin}${r.data.path}`;
      setNewLink(url);
      try { await navigator.clipboard.writeText(url); toast.success("Odkaz vytvorený a skopírovaný"); }
      catch { toast.success("Odkaz vytvorený"); }
      setShareLabel("");
      await loadShares();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCreatingShare(false); }
  };

  const revokeShare = async (sid) => {
    if (!window.confirm("Zrušiť tento odkaz? Prestane fungovať okamžite.")) return;
    try { await api.post(`/company/shares/${sid}/revoke`); await loadShares(); toast.success("Odkaz zrušený"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Skopírované"); } catch { toast.error("Kopírovanie zlyhalo"); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.put(`/company/candidate/${id}/status`, { status });
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await api.post(`/company/candidate/${id}/notes`, { text: noteText });
      setNoteText("");
      await loadNotes();
      toast.success("Poznámka pridaná");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingNote(false); }
  };

  const saveEdit = async (noteId) => {
    try {
      await api.put(`/company/notes/${noteId}`, { text: editText });
      setEditId(null); setEditText("");
      await loadNotes();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const removeNote = async (noteId) => {
    if (!window.confirm("Vymazať túto poznámku?")) return;
    try {
      await api.delete(`/company/notes/${noteId}`);
      await loadNotes();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openCv = async () => {
    try {
      const res = await api.get(`/company/candidate/${id}/cv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch { toast.error("Pôvodné CV nie je dostupné"); }
  };

  const send = async () => {
    setSending(true);
    try {
      await api.post(`/company/contact/${id}`, { message: msg });
      toast.success("Správa odoslaná kandidátovi");
      setOpen(false);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const doAction = async (action, confirmMsg, successMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await api.post(`/company/candidate/${id}/${action}`);
      toast.success(successMsg);
      await load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!window.confirm("Naozaj NATRVALO vymazať celý profil bez zanechania stopy? Vymažú sa všetky údaje, poznámky aj CV. Táto akcia je nevratná.")) return;
    setBusy(true);
    try {
      await api.delete(`/company/candidate/${id}`);
      toast.success("Profil bol natrvalo vymazaný");
      nav("/company/database");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); setBusy(false); }
  };

  if (p === false) return <CompanyShell><p className="text-slate-500">Kandidát sa nenašiel.</p></CompanyShell>;
  if (!p) return <CompanyShell><div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-700" /></div></CompanyShell>;

  const sectionVisible = {
    provenance: true,
    ai_summary: true,
    suitable_positions: !p.anonymized && p.suitable_positions?.length > 0,
    contact: !p.anonymized,
    pipeline: !p.anonymized,
    positions: !p.anonymized,
    history: true,
    consent: !p.anonymized,
    notes: !p.anonymized || !!p.shredded,
    share: !p.anonymized,
    photo: !p.anonymized,
    areas: !p.anonymized,
    gdpr: !p.anonymized && !!gdpr,
    data_mgmt: true,
  };
  const visibleOrder = sectionOrder.filter((id) => sectionVisible[id]);
  const onReorderVisible = (nv) => setSectionOrder(applyVisibleReorder(sectionOrder, nv, (id) => sectionVisible[id]));

  const refreshAll = async () => { await load(); setHistKey((k) => k + 1); window.dispatchEvent(new Event("db-changed")); };
  const sectionEls = {
    positions: <PositionsPanel p={p} onChanged={refreshAll} />,
    history: <StatusHistory profileId={p.id} refreshKey={histKey} />,
    provenance: (
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5" data-testid="provenance-card">
        <h3 className="font-head font-semibold flex items-center gap-2"><Layers size={17} className="text-slate-500" /> Pôvod dát <InfoTip text={"Jasne rozlišujeme, odkiaľ údaje pochádzajú."} /></h3>
        <div className="mt-3 space-y-2 text-sm">
          <div className="rounded-2xl bg-slate-50 border border-slate-100 p-3">
            <div className="flex items-center gap-1.5 font-semibold text-slate-700"><FileText size={15} className="text-slate-500" /> Pôvodné CV (zdroj)</div>
            {!p.anonymized && p.cv_path ? (
              <button data-testid="view-cv-btn" onClick={openCv} className="mt-1.5 inline-flex items-center gap-1.5 text-blue-700 text-xs font-semibold hover:underline">
                <ExternalLink size={13} /> Zobraziť pôvodné CV{p.original_filename ? ` (${p.original_filename})` : ""}
              </button>
            ) : <span className="text-xs text-slate-400 mt-1 inline-block">Nedostupné</span>}
          </div>
          <div className="rounded-2xl bg-blue-50 border border-blue-100 p-3">
            <div className="flex items-center gap-1.5 font-semibold text-blue-700"><Sparkles size={15} /> AI-generované <InfoTip text="Profil, skóre, zhrnutie a zručnosti vytvorila AI z CV." /></div>
          </div>
          <div className="rounded-2xl bg-amber-50 border border-amber-100 p-3">
            <div className="flex items-center gap-1.5 font-semibold text-amber-700"><Lock size={15} /> Recruiter poznámky <InfoTip text="Interné, iba pre vašu firmu — nikdy nie pre kandidáta." /></div>
          </div>
        </div>
      </div>
    ),
    ai_summary: (
      <div className="rounded-3xl bg-blue-700 text-white p-6">
        <div className="flex items-center gap-2 text-blue-200"><Sparkles size={16} /><span className="text-xs font-bold uppercase tracking-[0.15em]">AI Summary</span></div>
        <p className="mt-3 text-blue-50 leading-relaxed">{p.about}</p>
      </div>
    ),
    suitable_positions: (
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-6">
        <h3 className="font-head font-semibold text-lg mb-4">Vhodné pozície</h3>
        <div className="space-y-4">
          {(p.suitable_positions || []).map((pos, i) => (
            <div key={i}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-800">{pos.title}</span>
                <span className={`font-head font-bold ${pos.match >= 90 ? "text-emerald-600" : "text-blue-700"}`}>{pos.match}%</span>
              </div>
              {(pos.reasons || []).slice(0, 3).map((r, j) => (
                <div key={j} className="flex gap-1.5 text-xs text-slate-600 mt-1"><Check size={13} className="text-emerald-500 mt-0.5 flex-shrink-0" />{r}</div>
              ))}
              {(pos.missing || []).slice(0, 2).map((r, j) => (
                <div key={j} className="flex gap-1.5 text-xs text-slate-500 mt-1"><TriangleAlert size={12} className="text-amber-500 mt-0.5 flex-shrink-0" />{r}</div>
              ))}
            </div>
          ))}
        </div>
      </div>
    ),
    contact: (
      <button data-testid="contact-candidate-btn" onClick={() => setOpen(true)}
        className="w-full inline-flex items-center justify-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-6 py-4 font-semibold transition-colors active:scale-95">
        <Mail size={18} /> Kontaktovať kandidáta
      </button>
    ),
    pipeline: (
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5" data-testid="pipeline-selector">
        <h3 className="font-head font-semibold mb-3 flex items-center gap-2">Status kandidáta <InfoTip text="Jeden status kandidáta prepojený s GDPR. Pri udelení/odvolaní súhlasu sa mení automaticky." /></h3>
        <div className="grid grid-cols-1 gap-2">
          {PIPELINE_OPTS.map((s) => {
            const active = (p.pipeline_status || "new") === s.v;
            return (
              <button key={s.v} data-testid={`pipeline-${s.v}`} onClick={() => {
                if (s.v === "rejected_shredded" && !window.confirm(REJECT_CONFIRM)) return;
                setPipeline(s.v);
              }} disabled={pipelineBusy}
                className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${active ? `${s.cls} text-white` : "bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100"}`}>
                <span className={`h-2.5 w-2.5 rounded-full ${active ? "bg-white" : s.cls}`} /> {s.l}
              </button>
            );
          })}
        </div>
      </div>
    ),
    consent: (
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5" data-testid="consent-panel">
        <h3 className="font-head font-semibold flex items-center gap-2"><ShieldAlert size={17} className="text-slate-500" /> GDPR — Databáza kandidátov</h3>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-sm text-slate-500">Súhlas na uchovanie (2 roky)</span>
          <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${(GDPR_BADGE[gdprStatus] || GDPR_BADGE.NONE).cls}`} data-testid="consent-status-badge">
            {(GDPR_BADGE[gdprStatus] || GDPR_BADGE.NONE).l}
          </span>
        </div>
        {gdprStatus === "EXPIRING_SOON" && (
          <div className="mt-3 rounded-xl bg-amber-50 border border-amber-100 px-3 py-2 text-xs text-amber-700">
            Uchovávanie kandidáta v databáze vyprší o menej ako 2 mesiace.
          </div>
        )}
        <div className="mt-3 space-y-2" data-testid="consent-list">
          {consents.length === 0 && <p className="text-sm text-slate-400">Zatiaľ žiadny súhlas.</p>}
          {consents.slice(0, 4).map((c) => (
            <div key={c.id} className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-xs" data-testid={`consent-item-${c.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-700">{(GDPR_BADGE[c.status_computed] || GDPR_BADGE.NONE).l}</span>
                {(c.status_computed === "ACTIVE" || c.status_computed === "EXPIRING_SOON") && (
                  <button data-testid={`revoke-consent-${c.id}`} onClick={() => revokeConsent(c.id)} className="text-red-600 hover:underline font-semibold">Odvolať</button>
                )}
              </div>
              <div className="text-slate-400 mt-1">
                {c.granted_at ? `Udelený ${new Date(c.granted_at).toLocaleDateString("sk-SK")}` : "Čaká na udelenie"}
                {c.expires_at ? ` · platný do ${new Date(c.expires_at).toLocaleDateString("sk-SK")}` : ""}
              </div>
            </div>
          ))}
        </div>
        <button data-testid="request-consent-btn" onClick={requestConsent}
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-700 hover:bg-blue-100 transition-colors active:scale-95">
          <Mail size={15} /> {gdprStatus === "EXPIRING_SOON" || gdprStatus === "EXPIRED" ? "Vyžiadať nový súhlas" : "Vyžiadať súhlas od kandidáta"}
        </button>
        <button data-testid="preview-consent-btn" onClick={() => openPreview("consent_request")}
          className="mt-2 w-full text-xs font-semibold text-slate-500 hover:text-blue-700">Zobraziť náhľad emailu</button>
      </div>
    ),
    notes: (
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5" data-testid="notes-section">
        <h3 className="font-head font-semibold flex items-center gap-2"><Lock size={16} className="text-slate-500" /> Interné poznámky <InfoTip text={"Viditeľné iba pre vašu firmu. Nikdy nie pre kandidáta ani vo verejnom profile."} /></h3>
        <div className="mt-3 flex gap-2">
          <textarea data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2}
            placeholder="Pridať poznámku…"
            className="flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
        </div>
        <button data-testid="add-note-btn" onClick={addNote} disabled={savingNote || !noteText.trim()}
          className="mt-2 inline-flex items-center gap-1.5 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-4 py-2 text-sm font-semibold transition-colors active:scale-95 disabled:opacity-50">
          {savingNote ? <Loader2 className="animate-spin" size={15} /> : <MessageSquarePlus size={15} />} Pridať poznámku
        </button>
        <div className="mt-4 space-y-3" data-testid="notes-list">
          {notes.length === 0 && <p className="text-sm text-slate-400">Zatiaľ žiadne poznámky.</p>}
          {notes.map((n) => (
            <div key={n.id} data-testid={`note-${n.id}`} className="rounded-xl bg-slate-50 border border-slate-100 p-3">
              {editId === n.id ? (
                <>
                  <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={2}
                    className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-blue-600" />
                  <div className="mt-2 flex gap-2">
                    <button data-testid={`save-note-${n.id}`} onClick={() => saveEdit(n.id)} className="rounded-full bg-blue-700 text-white px-3 py-1 text-xs font-semibold">Uložiť</button>
                    <button onClick={() => setEditId(null)} className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600">Zrušiť</button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{n.text}</p>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs text-slate-400">{n.author_name} · {new Date(n.created_at).toLocaleString("sk-SK")}</span>
                    {n.can_edit && (
                      <span className="flex gap-1">
                        <button data-testid={`edit-note-${n.id}`} onClick={() => { setEditId(n.id); setEditText(n.text); }} className="p-1 text-slate-400 hover:text-blue-700"><Pencil size={14} /></button>
                        <button data-testid={`delete-note-${n.id}`} onClick={() => removeNote(n.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    ),
    share: (
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5" data-testid="share-section">
        <h3 className="font-head font-semibold flex items-center gap-2"><Share2 size={17} className="text-slate-500" /> Zdieľať s hiring managerom <InfoTip text={<>Vytvorí odkaz <span className="font-semibold">iba na čítanie</span> s časovým obmedzením. Hiring manager nevidí interné poznámky, status ani vašu databázu.</>} /></h3>
        <div className="mt-3 space-y-2">
          <input data-testid="share-label-input" value={shareLabel} onChange={(e) => setShareLabel(e.target.value)}
            placeholder="Označenie (napr. meno hiring managera) — voliteľné"
            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
          <div className="flex gap-2">
            <select data-testid="share-days-select" value={shareDays} onChange={(e) => setShareDays(e.target.value)}
              className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-600 bg-white">
              <option value={7}>Platnosť 7 dní</option>
              <option value={14}>Platnosť 14 dní</option>
              <option value={30}>Platnosť 30 dní</option>
              <option value={60}>Platnosť 60 dní</option>
              <option value={90}>Platnosť 90 dní</option>
            </select>
            <button data-testid="create-share-btn" onClick={createShare} disabled={creatingShare}
              className="flex-1 inline-flex items-center justify-center gap-1.5 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-4 py-2 text-sm font-semibold transition-colors active:scale-95 disabled:opacity-50">
              {creatingShare ? <Loader2 className="animate-spin" size={15} /> : <Share2 size={15} />} Vytvoriť odkaz
            </button>
          </div>
        </div>
        {newLink && (
          <div className="mt-3 rounded-xl bg-blue-50 border border-blue-100 p-3" data-testid="new-share-link">
            <p className="text-xs font-semibold text-blue-700 mb-1">Nový odkaz (skopírujte a pošlite):</p>
            <div className="flex items-center gap-2">
              <input readOnly value={newLink} className="flex-1 rounded-lg border border-blue-200 bg-white px-2 py-1.5 text-xs text-slate-700 outline-none" />
              <button data-testid="copy-share-link-btn" onClick={() => copyLink(newLink)} className="p-2 rounded-lg bg-blue-700 text-white hover:bg-blue-800"><Copy size={14} /></button>
            </div>
          </div>
        )}
        <div className="mt-4 space-y-2" data-testid="shares-list">
          {shares.filter((s) => s.state === "active").length === 0 && <p className="text-sm text-slate-400">Žiadne aktívne zdieľania.</p>}
          {shares.map((s) => (
            <div key={s.id} data-testid={`share-${s.id}`} className="flex items-center justify-between rounded-xl bg-slate-50 border border-slate-100 p-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-700 truncate">
                  {s.label || "Zdieľaný odkaz"}
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${s.state === "active" ? "bg-emerald-100 text-emerald-700" : s.state === "expired" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-500"}`}>
                    {s.state === "active" ? "Aktívny" : s.state === "expired" ? "Vypršal" : "Zrušený"}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-400 mt-0.5">
                  <Clock size={11} /> do {new Date(s.expires_at).toLocaleDateString("sk-SK")} · {s.views} zobrazení
                </div>
              </div>
              {s.state === "active" && (
                <button data-testid={`revoke-share-${s.id}`} onClick={() => revokeShare(s.id)} className="p-1.5 text-slate-400 hover:text-red-600" title="Zrušiť odkaz"><XCircle size={16} /></button>
              )}
            </div>
          ))}
        </div>
      </div>
    ),
    photo: (
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5" data-testid="photo-section">
        <h3 className="font-head font-semibold flex items-center gap-2"><Layers size={17} className="text-slate-500" /> Profilová fotografia <InfoTip text={p.photo_url ? "Reálna fotografia je priradená." : "Kandidát nemá fotografiu (zobrazuje sa sivá silueta). Fotku môžete nahrať ručne."} /></h3>
        <input ref={photoRef} type="file" accept="image/*" hidden data-testid="photo-input" onChange={uploadPhoto} />
        <button data-testid="upload-photo-btn" onClick={() => photoRef.current?.click()} disabled={uploadingPhoto}
          className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50">
          {uploadingPhoto ? <Loader2 className="animate-spin" size={15} /> : <Camera size={15} />} {p.photo_url ? "Zmeniť fotografiu" : "Nahrať fotografiu"}
        </button>
      </div>
    ),
    areas: (
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5" data-testid="area-section">
        <h3 className="font-head font-semibold flex items-center gap-2"><Layers size={17} className="text-slate-500" /> Oblasti <InfoTip text={"Kandidát môže patriť do viacerých oblastí. Kliknutím pridáte alebo odstránite oblasť."} /></h3>
        {(p.areas || []).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="candidate-areas">
            {(p.areas || []).map((a) => (
              <span key={a} className="inline-flex items-center gap-1 rounded-full bg-blue-600 px-2.5 py-1 text-xs font-semibold text-white">{a}</span>
            ))}
          </div>
        )}
        {areas.length === 0 ? (
          <p className="mt-3 text-[11px] text-amber-600">Zatiaľ nemáte vytvorené žiadne oblasti. Vytvorte ich v databáze kandidátov cez „+ Pridať oblasť".</p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="area-toggle-list">
            {areas.map((a) => {
              const on = (p.areas || []).includes(a);
              return (
                <button key={a} data-testid={`toggle-area-${a}`} onClick={() => changeArea(a)} disabled={savingArea}
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors disabled:opacity-50 ${on ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
                  {on ? "✓ " : "+ "}{a}
                </button>
              );
            })}
          </div>
        )}
      </div>
    ),
    gdpr: gdpr && (
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5" data-testid="gdpr-section">
        <h3 className="font-head font-semibold flex items-center gap-2"><ScrollText size={17} className="text-slate-500" /> GDPR a pôvod údajov <InfoTip text={<>Zaznamenajte zdroj, účel a právny základ spracovania. Neúplné polia sú označené ako <span className="font-semibold">NEZAZNAMENANÉ</span>.</>} /></h3>
        <div className="mt-4 space-y-3 text-sm">
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Zdroj kandidáta</span>
            <input data-testid="gdpr-source" value={gdpr.source} onChange={(e) => setGdpr({ ...gdpr, source: e.target.value })}
              placeholder="napr. LinkedIn, odporúčanie, kariérna stránka…"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
            {!gdpr.source && <span className="text-[11px] text-amber-600 mt-0.5 inline-block">NEZAZNAMENANÉ</span>}
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Typ prihlášky</span>
            <select data-testid="gdpr-application-type" value={gdpr.application_type} onChange={(e) => setGdpr({ ...gdpr, application_type: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-blue-600 bg-white">
              {APP_TYPE_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Účel spracovania</span>
            <input data-testid="gdpr-purpose" value={gdpr.purpose} onChange={(e) => setGdpr({ ...gdpr, purpose: e.target.value })}
              placeholder="napr. Nábor na konkrétnu pozíciu, talent pool…"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
            {!gdpr.purpose && <span className="text-[11px] text-amber-600 mt-0.5 inline-block">NEZAZNAMENANÉ</span>}
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Právny základ (GDPR čl. 6)</span>
            <select data-testid="gdpr-legal-basis" value={gdpr.legal_basis} onChange={(e) => setGdpr({ ...gdpr, legal_basis: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-blue-600 bg-white">
              {LEGAL_BASIS_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Retention dátum (do kedy uchovávame)</span>
            <input data-testid="gdpr-retention-date" type="date" value={gdpr.retention_date} onChange={(e) => setGdpr({ ...gdpr, retention_date: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
            {!gdpr.retention_date && <span className="text-[11px] text-amber-600 mt-0.5 inline-block">NEZAZNAMENANÉ</span>}
          </label>
          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Stav Privacy Notice</span>
            <select data-testid="gdpr-privacy-status" value={gdpr.privacy_notice_status} onChange={(e) => setGdpr({ ...gdpr, privacy_notice_status: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 outline-none focus:border-blue-600 bg-white">
              {PRIVACY_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
            {p.privacy_notice_sent_at && (
              <span className="text-[11px] text-emerald-600 mt-1 inline-block" data-testid="privacy-sent-info">
                Naposledy odoslané {new Date(p.privacy_notice_sent_at).toLocaleString("sk-SK")}{p.privacy_notice_email ? ` na ${p.privacy_notice_email}` : ""}
              </span>
            )}
          </label>
          <button data-testid="open-privacy-notice-btn" type="button"
            onClick={() => { setPrivacyEmail(p.privacy_notice_email || ""); setPrivacyOpen(true); }}
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm font-semibold text-blue-700 hover:bg-blue-100 transition-colors active:scale-95">
            <Mail size={15} /> Odoslať Privacy Notice email
          </button>
        </div>
        <button data-testid="save-gdpr-btn" onClick={saveGdpr} disabled={savingGdpr}
          className="mt-4 inline-flex items-center gap-1.5 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-4 py-2 text-sm font-semibold transition-colors active:scale-95 disabled:opacity-50">
          {savingGdpr ? <Loader2 className="animate-spin" size={15} /> : <Check size={15} />} Uložiť GDPR údaje
        </button>
      </div>
    ),
    data_mgmt: (
      <div className="rounded-3xl bg-white border border-slate-200 shadow-sm p-5">
        <h3 className="font-head font-semibold flex items-center gap-2"><ShieldAlert size={17} className="text-slate-500" /> Správa údajov <InfoTip text={"Archivácia nemaže kandidáta. Anonymizácia natrvalo vymaže osobné údaje podľa retention policy. Vymazanie odstráni celý profil bez zanechania stopy."} /></h3>
        <div className="mt-4 flex flex-wrap gap-2">
          {!p.archived ? (
            <button data-testid="archive-btn" onClick={() => doAction("archive", null, "Kandidát archivovaný")} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50">
              <Archive size={15} /> Archivovať
            </button>
          ) : !p.anonymized ? (
            <button data-testid="unarchive-btn" onClick={() => doAction("unarchive", null, "Kandidát obnovený")} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50">
              <ArchiveRestore size={15} /> Obnoviť
            </button>
          ) : null}
          {!p.anonymized && (
            <button data-testid="anonymize-btn"
              onClick={() => doAction("anonymize", "Naozaj natrvalo anonymizovať osobné údaje tohto kandidáta? Táto akcia je nevratná.", "Údaje anonymizované")} disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-semibold text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-50">
              <EyeOff size={15} /> Anonymizovať údaje
            </button>
          )}
          <button data-testid="delete-btn" onClick={doDelete} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-100 transition-colors disabled:opacity-50">
            <Trash2 size={15} /> Vymazať celý profil
          </button>
        </div>
      </div>
    ),
  };

  if (p.shredded) return (
    <CompanyShell>
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-4"><ArrowLeft size={16} /> Späť</button>
      <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
        <ShreddedCard p={p} />
        <div>{sectionEls.notes}</div>
      </div>
    </CompanyShell>
  );

  const atInterview = p.pipeline_status === "interview" || (p.positions || []).some((x) => x.status === "interview");
  if (atInterview && searchParams.get("full") !== "1") return (
    <CompanyShell>
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-4"><ArrowLeft size={16} /> Späť</button>
      <InterviewPage p={p} onShowProfile={() => setSearchParams({ full: "1" })} />
    </CompanyShell>
  );
  const showDecision = !decisionClosed && !p.anonymized && (p.pipeline_status || "new") === "new" && !p.first_decision_at;

  return (
    <CompanyShell>
      {showDecision && <FirstDecisionDialog name={p.name} onClose={() => setDecisionClosed(true)}
        onDecide={(v) => { setDecisionClosed(true); setPipeline(v); }} />}
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-4"><ArrowLeft size={16} /> Späť</button>

      <div className="mb-4"><StatusBadge profile={p} size="lg" /></div>

      {p.anonymized && (
        <div className="mb-4 flex items-start gap-2 rounded-2xl bg-slate-100 border border-slate-200 p-4 text-sm text-slate-600" data-testid="anonymized-note">
          <EyeOff size={18} className="flex-shrink-0 mt-0.5" />
          <span>{p.anonymized_note || "Osobné údaje boli vymazané podľa retention policy."}</span>
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
        <div className="max-w-md w-full space-y-5">
          <div className="rounded-2xl bg-blue-50 border border-blue-100 px-4 py-2.5 flex items-center gap-2 text-sm font-medium text-blue-700" data-testid="ai-generated-label">
            <Sparkles size={15} /> Tento profil (skóre, zhrnutie, zručnosti) vytvorila AI z pôvodného CV.
          </div>
          <JobCompare base={`/company/candidate/${p.id}`} jobsUrl="/company/jobs" careerScore={p.career_score} />
          <PassportView p={p} showInsights hideAbout />
        </div>

        <div className="space-y-5 lg:sticky lg:top-6">
          <div className="flex justify-end -mb-1"><ResetOrderButton onReset={resetSections} testId="reset-sections-btn" label="Obnoviť poradie sekcií" /></div>
          <Reorder.Group as="div" axis="y" values={visibleOrder} onReorder={onReorderVisible} className="space-y-5" data-testid="candidate-sections">
            {visibleOrder.map((id) => (
              <SortableCard key={id} id={id}>{sectionEls[id]}</SortableCard>
            ))}
          </Reorder.Group>

          {preview && (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4" data-testid="email-preview-modal" onClick={() => setPreview(null)}>
              <div className="w-full max-w-lg rounded-3xl bg-white shadow-xl overflow-hidden flex flex-col max-h-[88vh]" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
                  <span className="text-sm font-bold text-slate-700">Náhľad a úprava emailu</span>
                  <button data-testid="preview-close" onClick={() => setPreview(null)} className="text-slate-400 hover:text-slate-700"><XCircle size={18} /></button>
                </div>
                <div className="p-5 space-y-3 overflow-y-auto">
                  <div className="text-xs text-slate-500"><span className="font-semibold text-slate-600">Komu:</span> {preview.to_email || "—"}</div>
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-600">Predmet</span>
                    <input data-testid="preview-subject" value={preview.subject} onChange={(e) => setPreview((p) => ({ ...p, subject: e.target.value }))}
                      className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-600" />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-slate-600">Telo emailu</span>
                    <textarea data-testid="preview-body" value={preview.body_text} onChange={(e) => setPreview((p) => ({ ...p, body_text: e.target.value }))}
                      rows={10} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm leading-relaxed outline-none focus:border-blue-600" />
                  </label>
                  {preview.has_cta && <p className="text-[11px] text-blue-600">K emailu sa automaticky pridá tlačidlo „UDELIŤ SÚHLAS" s odkazom pre kandidáta.</p>}
                </div>
                <div className="px-5 py-3 border-t border-slate-200 flex gap-2">
                  <button data-testid="preview-cancel" onClick={() => setPreview(null)}
                    className="flex-1 rounded-full border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Zrušiť</button>
                  <button data-testid="preview-send" onClick={sendFromPreview} disabled={moveBusy}
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors active:scale-95 disabled:opacity-60">
                    {moveBusy ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />} Odoslať
                  </button>
                </div>
              </div>
            </div>
          )}

          {moveModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="move-modal" onClick={() => setMoveModal(null)}>
              <div className="w-full max-w-md rounded-3xl bg-white shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
                <h3 className="font-head text-lg font-bold">Presun do databázy kandidátov</h3>
                <p className="text-sm text-slate-500 mt-1">Kandidát bol zamietnutý pre túto pozíciu. Na uchovanie v databáze pre budúce ponuky (2 roky) je potrebný jeho súhlas.</p>

                {moveModal.needs_consent ? (
                  <div className="mt-4 rounded-2xl bg-amber-50 border border-amber-100 p-4">
                    <p className="text-sm font-semibold text-amber-800">Kandidát nemá platný súhlas.</p>
                    <p className="text-xs text-amber-700 mt-1">Odošlite žiadosť o súhlas — kandidát ho udelí kliknutím v emaile. Do udelenia nie je aktívne uchovávaný.</p>
                    <button data-testid="move-request-consent" onClick={async () => { setMoveBusy(true); await requestConsent(); setMoveBusy(false); setMoveModal((m) => ({ ...m, requested: true })); }}
                      disabled={moveBusy}
                      className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-blue-700 text-white hover:bg-blue-800 rounded-full px-5 py-3 font-semibold transition-colors active:scale-95 disabled:opacity-60">
                      {moveBusy ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />} {moveModal.requested ? "Žiadosť odoslaná ✓" : "Odoslať odpoveď + vyžiadať súhlas (2 roky)"}
                    </button>
                    <p className="text-[11px] text-amber-600 mt-2">Email obsahuje odpoveď aj odkaz na udelenie súhlasu na jednom mieste.</p>
                    <button data-testid="move-preview-consent" onClick={() => openPreview("consent_request")}
                      className="mt-2 text-xs font-semibold text-blue-700 hover:underline">Zobraziť náhľad emailu</button>
                  </div>
                ) : (
                  <div className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-100 p-4">
                    <p className="text-sm font-semibold text-emerald-800">Kandidát má platný súhlas — uchovávaný v databáze.</p>
                    <button data-testid="move-send-reply" onClick={() => sendReply("rejection_with_retention")} disabled={moveBusy}
                      className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-emerald-600 text-white hover:bg-emerald-700 rounded-full px-5 py-3 font-semibold transition-colors active:scale-95 disabled:opacity-60">
                      {moveBusy ? <Loader2 className="animate-spin" size={16} /> : <Mail size={16} />} Odoslať odpoveď kandidátovi
                    </button>
                    <button data-testid="move-preview-reply" onClick={() => openPreview("rejection_with_retention")}
                      className="mt-2 text-xs font-semibold text-emerald-700 hover:underline">Zobraziť náhľad emailu</button>
                  </div>
                )}

                <button data-testid="move-close" onClick={() => setMoveModal(null)}
                  className="mt-4 w-full rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                  Hotovo
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md bg-white rounded-3xl p-6" onClick={(e) => e.stopPropagation()} data-testid="contact-modal">
            <h2 className="font-head text-xl font-bold">Kontaktovať {p.name}</h2>
            <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={5} data-testid="contact-message"
              className="mt-4 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
            <div className="mt-4 flex gap-3">
              <button onClick={() => setOpen(false)} className="flex-1 rounded-full border border-slate-300 py-3 font-semibold text-slate-700 hover:bg-slate-50 transition-colors">Zrušiť</button>
              <button data-testid="send-contact-btn" onClick={send} disabled={sending}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-700 text-white rounded-full py-3 font-semibold hover:bg-blue-800 transition-colors disabled:opacity-60">
                {sending ? <Loader2 className="animate-spin" size={17} /> : <Send size={17} />} Odoslať
              </button>
            </div>
          </div>
        </div>
      )}
      {privacyOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" onClick={() => setPrivacyOpen(false)}>
          <div className="w-full max-w-md bg-white rounded-3xl p-6" onClick={(e) => e.stopPropagation()} data-testid="privacy-notice-modal">
            <h2 className="font-head text-xl font-bold flex items-center gap-2"><Mail size={20} className="text-blue-700" /> Privacy Notice</h2>
            <p className="mt-2 text-sm text-slate-600">Kandidátovi pošleme GDPR informáciu o účele spracúvania, právnom základe, dobe uchovávania a jeho právach. Zadajte email príjemcu.</p>
            <label className="block mt-4">
              <span className="text-sm font-semibold text-slate-700">Email kandidáta</span>
              <input data-testid="privacy-email-input" type="email" value={privacyEmail} onChange={(e) => setPrivacyEmail(e.target.value)}
                placeholder="kandidat@email.sk"
                className="mt-1.5 w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100" />
            </label>
            <p className="mt-2 text-[11px] text-amber-600">Pozn.: v testovacom (sandbox) režime Resend doručí email iba na overenú adresu vlastníka účtu, kým sa neoverí doména.</p>
            <div className="mt-4 flex gap-3">
              <button onClick={() => setPrivacyOpen(false)} className="flex-1 rounded-full border border-slate-300 py-3 font-semibold text-slate-700 hover:bg-slate-50 transition-colors">Zrušiť</button>
              <button data-testid="send-privacy-btn" onClick={sendPrivacy} disabled={sendingPrivacy || !privacyEmail.trim()}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-700 text-white rounded-full py-3 font-semibold hover:bg-blue-800 transition-colors disabled:opacity-60">
                {sendingPrivacy ? <Loader2 className="animate-spin" size={17} /> : <Send size={17} />} Odoslať
              </button>
            </div>
          </div>
        </div>
      )}
    </CompanyShell>
  );
}


function ShreddedCard({ p }) {
  const rows = [
    ["Meno a priezvisko", p.name, "shredded-name"],
    ["Pozícia", (p.positions || []).map((x) => x.name).join(", ") || (p.areas || []).join(", ") || "—", "shredded-position"],
    ["Dátum pridania", p.created_at ? new Date(p.created_at).toLocaleDateString("sk-SK") : "—", "shredded-date"],
    ["Stav", "Zamietnutý", "shredded-status"],
  ];
  return (
    <div className="max-w-md w-full rounded-3xl bg-white border border-slate-200 shadow-sm p-6" data-testid="shredded-profile-card">
      <div className="flex items-center gap-2 rounded-2xl bg-red-50 border border-red-100 px-4 py-2.5 text-sm font-medium text-red-700">
        <EyeOff size={15} /> Osobné údaje boli nenávratne skartované.
      </div>
      <dl className="mt-5 space-y-4">
        {rows.map(([l, v, t]) => (
          <div key={t}>
            <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">{l}</dt>
            <dd data-testid={t} className="mt-0.5 font-semibold text-slate-800">{v}</dd>
          </div>
        ))}
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Career Score</dt>
          <dd className="mt-2" data-testid="shredded-career-score">
            {typeof p.career_score === "number" ? <ScoreRing value={p.career_score} size={72} stroke={6} showLabel={false} /> : "—"}
          </dd>
        </div>
      </dl>
    </div>
  );
}
